import { GameBankSheetCheckStatus, GameBankSheetStatus, GameCheckStatus } from "@/Api/gameBank";
import { cn } from "@/lib/utils";

export const pad2 = (value: number | string) => String(value).padStart(2, "0");

/** Cores da conferência: vermelho repetido, laranja 1 diferente, amarelo 2 diferentes, verde 3+ */
export const STATUS_STYLE: Record<
  GameCheckStatus,
  { label: string; box: string; dot: string }
> = {
  ok: {
    label: "OK",
    box: "bg-green-100 text-green-900 border-green-300 dark:bg-green-900/30 dark:text-green-200 dark:border-green-800",
    dot: "bg-green-500",
  },
  attention: {
    label: "Atenção",
    box: "bg-yellow-100 text-yellow-900 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-200 dark:border-yellow-800",
    dot: "bg-yellow-400",
  },
  high: {
    label: "Alta coincidência",
    box: "bg-orange-100 text-orange-900 border-orange-300 dark:bg-orange-900/30 dark:text-orange-200 dark:border-orange-800",
    dot: "bg-orange-500",
  },
  repeated: {
    label: "Repetido",
    box: "bg-red-100 text-red-900 border-red-300 dark:bg-red-900/30 dark:text-red-200 dark:border-red-800",
    dot: "bg-red-500",
  },
  invalid: {
    label: "Inválido",
    box: "bg-slate-100 text-red-700 border-slate-300 dark:bg-slate-800 dark:text-red-300 dark:border-slate-600",
    dot: "bg-slate-400",
  },
};

/** Etiqueta da loteria na cor dela (mesmo visual dos chips de loteria do sistema) */
export const LotteryBadge = ({
  name,
  color,
  className,
}: {
  name: string;
  color?: string | null;
  className?: string;
}) => (
  <span
    className={cn(
      "inline-flex items-center rounded-md px-2.5 py-1 text-xs font-bold uppercase text-white whitespace-nowrap",
      className
    )}
    style={{ backgroundColor: color || "#64748b" }}
  >
    {name}
  </span>
);

/** Bolinha numerada de uma dezena */
export const NumberChip = ({ value, color }: { value: number; color?: string | null }) => (
  <span
    className="inline-flex h-7 min-w-7 items-center justify-center rounded-full px-1 text-xs font-semibold text-white"
    style={{ backgroundColor: color || "#334155" }}
  >
    {pad2(value)}
  </span>
);

/** Status da planilha: Em andamento (editável), Finalizado (jogos no banco), Arquivado (fora da listagem) */
export const SHEET_STATUS: Record<GameBankSheetStatus, { label: string; className: string }> = {
  in_progress: {
    label: "Em andamento",
    className: "bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-800",
  },
  finished: {
    label: "Finalizado",
    className: "bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-200 dark:border-green-800",
  },
  archived: {
    label: "Arquivado",
    className: "bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600",
  },
};

/** Status da conferência: Pendente, Analisando (reservado), Analisado */
export const CHECK_STATUS: Record<GameBankSheetCheckStatus, { label: string; className: string }> = {
  pending: {
    label: "Conferência pendente",
    className: "bg-amber-50 text-amber-800 border-amber-300 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-800",
  },
  analyzing: {
    label: "Analisando",
    className: "bg-violet-50 text-violet-800 border-violet-300 dark:bg-violet-950/30 dark:text-violet-200 dark:border-violet-800",
  },
  analyzed: {
    label: "Analisado",
    className: "bg-teal-50 text-teal-800 border-teal-300 dark:bg-teal-950/30 dark:text-teal-200 dark:border-teal-800",
  },
};

const badgeBase = "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap";

export const SheetStatusBadge = ({ status, className }: { status: GameBankSheetStatus; className?: string }) => (
  <span className={cn(badgeBase, SHEET_STATUS[status].className, className)}>{SHEET_STATUS[status].label}</span>
);

export const CheckStatusBadge = ({ status, className }: { status: GameBankSheetCheckStatus; className?: string }) => (
  <span className={cn(badgeBase, CHECK_STATUS[status].className, className)}>{CHECK_STATUS[status].label}</span>
);

/** Data e hora no fuso local (dd/mm/aaaa hh:mm) */
export const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};
